import type { Brand, BrandSummary, Tag } from "./types";

type BrandForSeo = Pick<Brand, "name" | "shortSummary" | "country" | "region" | "priceLevel" | "tags" | "primaryCategory" | "scores" | "crowdSignals">;

export interface BrandSeoContent {
  intro: string;
  whyEarly: string;
  youthRelevance: string;
  discoveryEvidence: string[];
  radarAngles: string[];
  relatedSearchTerms: string[];
}

export function buildBrandSeoContent(brand: BrandForSeo): BrandSeoContent {
  const category = brand.primaryCategory?.name ?? tagNames(brand.tags, "primary_category")[0] ?? "brand";
  const styles = tagNames(brand.tags, "style_direction").slice(0, 3);
  const aesthetics = tagNames(brand.tags, "aesthetic").slice(0, 3);
  const materials = tagNames(brand.tags, "material").slice(0, 2);
  const style = styles[0] ?? aesthetics[0] ?? "emerging style";
  const place = [brand.region, brand.country].filter(Boolean).join(", ");
  const market = tagNames(brand.tags, "market")[0];
  const discovery = brand.scores.discovery ?? 0;
  const mainstream = brand.scores.mainstream ?? 100;
  const momentum = brand.scores.momentum ?? 0;
  const aestheticStrength = brand.scores.aestheticStrength ?? 0;
  const confidence = brand.scores.confidence ?? 0;
  const crowd = brand.crowdSignals?.counts;
  const positiveCrowd = (crowd?.early ?? 0) + (crowd?.would_wear ?? 0);
  const negativeCrowd = (crowd?.mainstream ?? 0) + (crowd?.not_it ?? 0);
  const strongestSignals = [...styles, ...aesthetics].slice(0, 4);

  const introParts = [
    `${brand.name} sits on the BeforeMainstream radar as an emerging ${category.toLowerCase()} name`,
    place ? `from ${place}` : null,
    `with a ${joinReadable(strongestSignals.length ? strongestSignals.map(lowerFirst) : [style.toLowerCase()])} direction`,
  ].filter(Boolean);

  const scoreGap = discovery - mainstream;
  const whyEarly = buildWhyEarly({
    name: brand.name,
    style,
    category,
    discovery,
    mainstream,
    momentum,
    scoreGap,
    strongestSignals,
  });

  const youthRelevance = [
    `${brand.name} may resonate with younger shoppers looking for ${style.toLowerCase()}`,
    materials.length ? `, ${joinReadable(materials.map(lowerFirst))}` : "",
    market ? `, and ${market.toLowerCase()}` : "",
    ` without relying only on familiar mass-market labels. ${audienceReason(category, style, aesthetics)}`,
  ].join("");

  const discoveryEvidence = [
    `Discovery signal: ${scoreWord(discovery)} (${discovery || "not scored"}).`,
    `Mainstream signal: ${scoreWord(mainstream)} (${mainstream || "not scored"}).`,
    `Momentum: ${scoreWord(momentum)} (${momentum || "not scored"}).`,
    aestheticStrength ? `Aesthetic clarity: ${scoreWord(aestheticStrength)} (${aestheticStrength}).` : null,
    scoreGap > 0 ? `Radar gap: discovery is ${scoreGap} point(s) above mainstream visibility.` : null,
    strongestSignals.length ? `Style signal: ${joinReadable(strongestSignals)}.` : null,
    confidence ? `Data confidence: ${scoreWord(confidence)} (${confidence}).` : null,
    positiveCrowd || negativeCrowd ? `Crowd signal: ${positiveCrowd} early/wear vote(s), ${negativeCrowd} mainstream/not-it vote(s).` : null,
  ].filter(Boolean) as string[];

  const radarAngles = [
    signalAngle("Category", category),
    strongestSignals.length ? signalAngle("Style read", joinReadable(strongestSignals)) : null,
    materials.length ? signalAngle("Material cue", joinReadable(materials)) : null,
    place ? signalAngle("Local context", place) : null,
    scoreGap > 0 ? signalAngle("Before-mainstream gap", `${scoreGap} point spread`) : null,
  ].filter(Boolean) as string[];

  return {
    intro: `${introParts.join(" ")}. ${brand.shortSummary ?? ""}`.trim(),
    whyEarly,
    youthRelevance,
    discoveryEvidence,
    radarAngles,
    relatedSearchTerms: [brand.name, category, ...styles.slice(0, 2), ...aesthetics.slice(0, 2), ...materials, market, place].filter(Boolean) as string[],
  };
}

export function buildTagIntro(tag: Tag, brands: BrandSummary[], kind: "category" | "style" | "tag"): string {
  const count = tag.brandCount ?? brands.length;
  const noun = count === 1 ? "brand" : "brands";
  const topNames = brands.slice(0, 3).map((brand) => brand.name);
  const examples = topNames.length ? ` including ${joinReadable(topNames)}` : "";
  const label = kind === "category" ? "category" : kind === "style" ? "style direction" : "signal";
  const base = tag.description ?? taxonomyDescription(tag, count, kind);
  return `${base} This ${label} currently tracks ${count} emerging ${noun}${examples}, sorted by crowd and discovery signals so newer names can surface before they become obvious.`;
}

export function taxonomyDescription(tag: Pick<Tag, "slug" | "name" | "type">, count?: number, kind?: "category" | "style" | "tag"): string {
  const specific = TAXONOMY_DESCRIPTIONS[tag.slug];
  if (specific) return specific;

  const total = count ? `${count} ` : "";
  const label = kind === "category" || tag.type === "primary_category" || tag.type === "secondary_category"
    ? "category"
    : kind === "style" || tag.type === "style_direction"
      ? "style direction"
      : "signal";
  return `${tag.name} is an active ${label} on BeforeMainstream for tracking ${total}emerging fashion labels before they become obvious.`;
}

const TAXONOMY_DESCRIPTIONS: Record<string, string> = {
  outerwear: "Outerwear is where emerging brands show technical taste fastest: shell shapes, fleeces, jackets, weather-ready layers, and the quiet details that move from niche trails to city uniforms.",
  tops: "Tops tracks the tees, knits, sweats, shirts, and graphic layers that usually travel quickest through youth wardrobes before a brand becomes widely known.",
  footwear: "Footwear follows independent sneaker, boot, runner, sandal, and technical shoe labels where silhouette, material, and subculture signal often appear before broad retail adoption.",
  sportswear: "Sportswear covers labels blending athletic references with everyday dressing: running culture, football shapes, warm-up pieces, tracksuits, technical fabric, and off-duty uniform energy.",
  pants: "Pants tracks the cargo, denim, work pant, and relaxed trouser labels that shape proportion and utility before they reach bigger stores.",
  shirts: "Shirts follows button-downs, overshirts, camp collars, and tailored casual layers from brands making everyday dressing feel sharper without becoming formal.",
  denim: "Denim tracks brands using washes, cuts, raw texture, and workwear references to make jeans and jackets feel current without chasing obvious trend cycles.",
  eyewear: "Eyewear follows independent frame makers and accessory-led labels where shape, proportion, and low-logo identity can define a look quickly.",
  outdoor: "Outdoor tracks trail-aware, weather-ready, and utility-led brands that sit between real gear and everyday style.",

  "street-classic": "Street classic tracks brands with durable youth appeal: graphic drops, skate references, hoodies, cargos, sneakers, denim, and pieces that feel familiar but still early.",
  "scandinavian-minimal": "Scandinavian minimal follows quiet labels built around restraint, functional cuts, muted palettes, and pieces that work because the details are dialed down.",
  "clean-basics": "Clean basics tracks brands making everyday staples feel considered: better proportions, lower branding, heavier fabrics, and wardrobe pieces that do not need a loud hook.",
  gorpcore: "Gorpcore follows the fashion edge of outdoor gear: shells, fleece, trail footwear, nylon, modular layers, and weather-ready details moving into everyday outfits.",
  "urban-outdoor": "Urban outdoor tracks brands that translate trail utility into city use, blending performance references with softer styling and wearable layers.",
  "experimental-design": "Experimental design follows labels using unusual shapes, distorted silhouettes, runway attitude, or concept-led product ideas before they become easy to categorize.",
  "minimal-streetwear": "Minimal streetwear tracks brands keeping the streetwear uniform but stripping it back through cleaner graphics, better cuts, and quieter branding.",
  "performance-casual": "Performance casual follows technical comfort, running references, and active materials used in clothes that still look natural away from sport.",
  "contemporary-menswear": "Contemporary menswear tracks modern independent labels balancing tailoring, utility, casual layers, and low-logo polish.",
  "modern-tailoring": "Modern tailoring follows brands softening suits, shirts, trousers, and jackets into pieces that feel wearable for everyday culture rather than office dress codes.",
  "outdoor-lifestyle": "Outdoor lifestyle tracks brands using nature, travel, trail, and cabin references as a style language rather than pure performance gear.",
  "quiet-performance": "Quiet performance follows low-logo technical clothing where function is present but not shouting: breathable fabrics, clean construction, and calm silhouettes.",
  "technical-minimalism": "Technical minimalism tracks brands that make performance details feel precise, matte, and architectural rather than sporty.",
  "slow-fashion": "Slow fashion follows brands where small runs, craft, natural materials, and considered production are part of the appeal.",
  "trail-culture": "Trail culture tracks brands connected to running, hiking, alpine, and outdoor communities before those signals flatten into general activewear.",
  "vintage-sportswear": "Vintage sportswear follows brands bringing retro athletic proportions, washed colour, old team references, and relaxed training pieces into current wardrobes.",
  "casual-tailoring": "Casual tailoring tracks softer shirts, trousers, jackets, and suits that sit between menswear tradition and everyday youth style.",
  "functional-everyday": "Functional everyday follows brands making useful, repeat-wear pieces with pockets, weather resistance, durable fabrics, and low-friction styling.",
  heritage: "Heritage tracks brands drawing from workwear, military, Ivy, outdoor, denim, and craft references without feeling like costume.",
  "japanese-utility": "Japanese utility follows labels shaped by precise construction, workwear references, roomy cuts, technical fabric, and calm but intentional details.",
  "low-logo-premium": "Low-logo premium tracks brands where the signal is fabric, cut, and restraint rather than obvious branding.",
  "nordic-functional": "Nordic functional follows cold-weather practicality, clean silhouettes, and useful materials translated into understated daily clothing.",
  "quiet-luxury": "Quiet luxury tracks labels using quality, proportion, and muted styling as the flex rather than logos or hype mechanics.",
  "raw-denim": "Raw denim follows brands where texture, fading, selvedge, and long-wear character make denim feel like a subculture again.",
  "retro-runner": "Retro runner tracks brands reviving old running silhouettes, lightweight shapes, sporty palettes, and trail-to-street footwear language.",
  "small-batch": "Small batch follows labels where limited production, local craft, or slower releases make the brand feel discovered rather than distributed.",
  "soft-utility": "Soft utility tracks functional clothing with relaxed proportions, calmer colours, and practical details that do not look overbuilt.",
  "utility-basics": "Utility basics follows everyday staples with workwear logic: durable fabric, pockets, simple shapes, and useful construction.",
  workwear: "Workwear tracks brands borrowing from uniforms, canvas, denim, chores, cargos, and labour references while making them feel current."
};

function tagNames(tags: Tag[], type: Tag["type"]): string[] {
  return tags.filter((tag) => tag.type === type).map((tag) => tag.name);
}

function scoreWord(score: number): string {
  if (score >= 80) return "strong";
  if (score >= 60) return "high";
  if (score >= 40) return "medium";
  if (score > 0) return "low";
  return "unknown";
}

function buildWhyEarly(input: {
  name: string;
  style: string;
  category: string;
  discovery: number;
  mainstream: number;
  momentum: number;
  scoreGap: number;
  strongestSignals: string[];
}): string {
  const signalText = input.strongestSignals.length
    ? `The strongest read is ${joinReadable(input.strongestSignals.map(lowerFirst))}`
    : `The strongest read is ${input.style.toLowerCase()}`;
  if (input.mainstream <= 45 && input.scoreGap >= 20) {
    return `${input.name} still reads early because discovery is ${scoreWord(input.discovery)} while mainstream visibility remains ${scoreWord(input.mainstream)}. ${signalText}, giving the ${input.category.toLowerCase()} label a clearer niche signal than mass-market signal.`;
  }
  if (input.mainstream <= 70) {
    return `${input.name} is gaining visibility, but it has not tipped fully mainstream yet. ${signalText}, and momentum is ${scoreWord(input.momentum)}, so it stays useful as a radar find rather than an obvious name.`;
  }
  return `${input.name} is closer to mainstream than most names here, so it stays on the radar only because its ${input.style.toLowerCase()} and ${input.category.toLowerCase()} signals remain specific enough to track.`;
}

function audienceReason(category: string, style: string, aesthetics: string[]): string {
  const texture = aesthetics.length ? ` The visual read leans ${joinReadable(aesthetics.slice(0, 2).map(lowerFirst))},` : "";
  if (/footwear|sneaker|shoe/i.test(category)) {
    return `${texture || " "}which makes it relevant for people tracking silhouette shifts before they land in larger sneaker rotations.`;
  }
  if (/outerwear|outdoor|sportswear/i.test(category) || /gorp|trail|performance|outdoor/i.test(style)) {
    return `${texture || " "}which makes it relevant for people watching technical and outdoor codes move into everyday dressing.`;
  }
  if (/street|workwear|denim/i.test(style)) {
    return `${texture || " "}which makes it relevant for people watching street, skate, denim, and workwear codes before they flatten into mainstream basics.`;
  }
  return `${texture || " "}which makes it relevant for people looking for a sharper style signal than the usual trend feed.`;
}

function signalAngle(label: string, value: string): string {
  return `${label}: ${value}.`;
}

function lowerFirst(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function joinReadable(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}
